<template>
  <tr>
    <td v-if="showStatusColumn" class="milestone-cell">
      <WdsStack v-if="milestone.status === MilestoneStatus.Completed" justify-center align-center>
        <WdsTooltip :text="$t('This milestone is completed and can’t be edited.')">
          <WdsIcon name="checkmark-filled" color="#047E43" :size="16"></WdsIcon>
        </WdsTooltip>
      </WdsStack>
      <WdsStack
        v-if="milestone.status === MilestoneStatus.PaymentRequested"
        justify-center
        align-center
      >
        <WdsTooltip
          global
          :text="
            $t('A payment request has been submitted for this milestone and is pending approval.')
          "
        >
          <WdsIcon
            data-test="milestone-payment-pending"
            name="warning"
            color="#FD8A33"
            :size="16"
          ></WdsIcon>
        </WdsTooltip>
      </WdsStack>
    </td>
    <td class="milestone-cell">
      <WdsInput
        v-if="milestoneBlockState === MilestoneEditState.editing"
        v-model="milestone.name"
        :disabled="inputsDisabled"
        :error="props.error?.name ?? undefined"
        placeholder="Milestone"
        data-test="milestone-name"
      />
      <WdsText v-else data-test="milestone-name">
        <span class="tw-font-normal">{{ milestone.name }}</span>
      </WdsText>
    </td>
    <td class="milestone-cell">
      <WdsInput
        v-if="milestoneBlockState === MilestoneEditState.editing"
        v-model="milestone.purchaseOrderNumber"
        :disabled="inputsDisabled"
        :placeholder="poNumberMandatory ? $t('12345678') : $t('12345678 (Optional)')"
        data-test="milestone-po-number"
      />
      <WdsText v-else data-test="milestone-po-number">
        <span class="tw-font-normal">{{ milestone.purchaseOrderNumber ?? '-' }}</span>
      </WdsText>
    </td>
    <td class="milestone-cell">
      <WdsInputDate
        v-if="milestoneBlockState === MilestoneEditState.editing"
        v-model="dueDate"
        :disabled="inputsDisabled"
        :error="props.error?.dueDate ?? undefined"
        format="yyyy-mm-dd"
        data-test="milestone-due-date"
      />
      <WdsText v-else data-test="milestone-due-date">
        <span class="tw-font-normal">{{ milestoneDetails.dueDate }}</span>
      </WdsText>
    </td>
    <td class="milestone-cell">
      <WdsInputCurrency
        v-if="milestoneBlockState === MilestoneEditState.editing"
        v-model="amountString"
        :config
        :disabled="inputsDisabled"
        placeholder="2000.00"
        data-test="milestone-amount"
      />
      <WdsText v-else data-test="milestone-amount">
        <span class="tw-font-normal">{{ amountString }}</span>
      </WdsText>
    </td>
    <td v-if="milestoneBlockState === MilestoneEditState.editing" class="milestone-cell">
      <WdsStack justify-center align-center>
        <WdsButtonGhost
          v-if="!inputsDisabled && milestoneBlockState === MilestoneEditState.editing"
          data-test="delete-milestone"
          @click.prevent="emit('delete', milestone)"
        >
          <WdsIcon name="trash-can" :size="18" />
        </WdsButtonGhost>
      </WdsStack>
    </td>
  </tr>
</template>

<script setup lang="ts">
import {
  WdsButtonGhost,
  WdsIcon,
  WdsInput,
  WdsInputCurrency,
  WdsInputDate,
  WdsStack,
  WdsText,
  WdsTooltip,
} from '@worksome/wds-components'
import { computed } from 'vue'
import { MilestoneEditState, MilestoneError } from '@/components/Milestones/types'
import useCurrency from '@/composables/useCurrency'
import { useTranslation } from '@/composables/translation'
import { MilestoneTypeFragment, MilestoneStatus } from '@/gql/graphql'

const emit = defineEmits<{
  delete: [milestone: MilestoneTypeFragment]
}>()

const { $t } = useTranslation()

const milestone = defineModel<MilestoneTypeFragment>({ required: true })

const milestoneDetails = computed(() => milestone.value.details)

const props = defineProps<{
  milestoneBlockState: MilestoneEditState
  showStatusColumn: boolean
  currency: string
  error?: MilestoneError
}>()

const poNumberMandatory = computed(() => currentAccount?.settings?.po_number_mandatory)

const amountString = computed({
  get: () => milestoneDetails.value.amount.toFixed(2),
  set: (val: string) => {
    const { details } = milestone.value
    details.amount = parseFloat(val)
  },
})

const dueDate = computed({
  get: () => milestoneDetails.value.dueDate,
  set: (val) => {
    const { details } = milestone.value
    details.dueDate = val
  },
})

// Makes currency reactive for the composable
const cur = computed(() => props.currency)
const { config } = useCurrency(cur)

const inputsDisabled = computed(
  () =>
    milestone.value.status === MilestoneStatus.PaymentRequested ||
    milestone.value.status === MilestoneStatus.Completed,
)
</script>

<style lang="scss" scoped>
.milestone-cell {
  padding: 4px 8px 4px 0;
  vertical-align: top;
  :deep(.wds-input-wrapper) {
    label {
      margin: 0;
    }
  }

  :deep(input) {
    max-width: 100%;
    padding-right: 0 !important;
  }
}
</style>
